import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { EnhancedSearchItem } from "../../product-list.types";
import { filterVariantsBySupplyTypes } from "../../../../../common/utils/supply-type-filter.utils";
import { filterVariantsWithValidSupplier } from "../../../../../common/utils/variant-offer.utils";
import { NotificationService } from "../../../../providers/notification/notification.service";

export interface AddToCartPayload {
    productId: string;
    variantId: string;
    quantity: number;
}

@Component({
    selector: "vsf-product-list-grid-item",
    templateUrl: "./product-list-grid-item.component.html",
    styleUrls: ["./product-list-grid-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListGridItemComponent {
    @Input() product: EnhancedSearchItem;
    @Input() isLoggedIn: boolean | null = false;
    @Input() supplyTypes: string[] = [];
    @Input() retailPriceRange:
        | { min: number | null; max: number | null; currency: string | null }
        | undefined;

    @Output() addToCartEvent = new EventEmitter<AddToCartPayload>();
    @Output() toggleOffersEvent = new EventEmitter<string>();

    constructor(private notificationService: NotificationService) {}

    get gtin(): string {
        return this.product?.customProductMappings?.gtin?.toString() || "";
    }

    get brand(): string {
        return this.product?.customProductMappings?.brand?.toString() || "";
    }

    get productLink(): (string | number)[] {
        const supplierId = this.product?.cheapestVariant?.supplierId;
        if (supplierId != null && String(supplierId).length > 0) {
            return ["/product", this.product.slug, supplierId];
        }
        return ["/product", this.product.slug];
    }

    get offerCount(): number {
        let variants = filterVariantsWithValidSupplier(
            this.product?.variants || [],
        );
        if (this.supplyTypes.length > 0) {
            variants = filterVariantsBySupplyTypes(variants, this.supplyTypes);
        }
        return Math.max(variants.length, this.product?.cheapestVariant ? 1 : 0);
    }

    get cheapestPrice(): number | null {
        const price = this.product?.cheapestVariant?.priceWithTax;
        return price == null || Number.isNaN(Number(price))
            ? null
            : Number(price);
    }

    get isHotOffer(): boolean {
        const supply = (
            this.product?.cheapestVariant?.supplyType || ""
        ).toLowerCase();
        return supply === "short-offer" || supply.includes("short");
    }

    get hasImage(): boolean {
        const preview = this.product?.productAsset?.preview;
        return !!preview && !String(preview).includes("placeholder");
    }

    get marginPercent(): number | null {
        if (!this.isLoggedIn) {
            return null;
        }
        const wholesale = this.cheapestPrice;
        const retail = this.retailMidCents;
        if (wholesale == null || retail == null || retail <= 0) {
            return null;
        }
        const pct = Math.round(((retail - wholesale) / retail) * 100);
        if (!Number.isFinite(pct) || pct <= 0) {
            return null;
        }
        return pct;
    }

    private get retailMidCents(): number | null {
        const range = this.retailPriceRange;
        if (!range || range.min == null || range.max == null) {
            return null;
        }
        const min = Number(range.min);
        const max = Number(range.max);
        if (Number.isNaN(min) || Number.isNaN(max) || min <= 0) {
            return null;
        }
        return (min + max) / 2;
    }

    copyGtin(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.gtin) {
            return;
        }
        navigator.clipboard.writeText(this.gtin).then(
            () => {
                this.notificationService
                    .success("EAN copied to clipboard")
                    .subscribe();
            },
            () => {
                this.notificationService
                    .error("Failed to copy EAN")
                    .subscribe();
            },
        );
    }
}
